// src/components/ui/FilterSlideOver.jsx
import React, { useEffect, useRef, useCallback, useState } from "react";
import { X, ChevronDown, ChevronUp, Filter as FilterIcon } from "lucide-react";

export function AccordionItem({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-visible">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-right"
      >
        <span className="font-medium text-gray-800">{title}</span>
        {open ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* overflow-visible when open so popovers aren't clipped */}
      <div
        className={`relative transition-[max-height,opacity] duration-300 ease-out ${
          open
            ? "max-h-[2000px] opacity-100 overflow-visible"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * FilterSlideOver
 */
export default function FilterSlideOver({
  isOpen,
  onClose,
  title = "فیلترها",
  widthClass = "w-[40vw] max-w-[720px] min-w-[320px]",
  onApply,
  onClear,
  showFooter = true,
  badgeCount,
  dir = "rtl",
  children,
}) {
  const panelRef = useRef(null);
  const [settled, setSettled] = useState(false);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [isOpen]);

  // Remove transform after slide-in so fixed/portaled popovers are not constrained
  useEffect(() => {
    let t;
    if (isOpen) {
      setSettled(false);
      t = setTimeout(() => setSettled(true), 320);
    } else {
      setSettled(false);
    }
    return () => clearTimeout(t);
  }, [isOpen]);

  const onBackdropClick = useCallback(() => onClose?.(), [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={onBackdropClick}
        aria-hidden={!isOpen}
      />

      {/* Panel (grid: header | scroll body | footer) */}
      <aside
        ref={panelRef}
        className={[
          "fixed inset-y-0 right-0 z-50 bg-white shadow-2xl transition-transform duration-300",
          widthClass,
          isOpen
            ? settled
              ? "transform-none"
              : "translate-x-0"
            : "translate-x-full",
          !settled && isOpen ? "will-change-transform" : "",
          "h-screen grid grid-rows-[auto,1fr,auto]", // <-- key change
        ].join(" ")}
        dir={dir}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header (row 1) */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <FilterIcon className="w-4 h-4" />
            <span className="font-semibold">{title}</span>
            {typeof badgeCount === "number" && badgeCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                {badgeCount} فعال
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100"
            aria-label="بستن"
            title="بستن"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (row 2) – this is the ONLY scroller */}
        <div className="min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3">
          {children}
        </div>

        {/* Footer (row 3) – not sticky, so it never covers content */}
        {showFooter && (
          <div className="px-4 py-4 border-t flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onClear}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            >
              پاکسازی فیلترها
            </button>
            <button
              type="button"
              onClick={onApply ?? onClose}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              اعمال و بستن
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
